/**
 * Тонкая обёртка над Яндекс.Метрикой.
 *
 * Counter id вшивается на этапе сборки webpack через DefinePlugin
 * (env YANDEX_METRIKA_ID). Пустое значение → весь публичный API здесь
 * no-op: <script> не вставляется, глобальный `ym` не появляется.
 *
 * Обёртка отдаёт только то, что нужно дашборду (initMetrika,
 * trackPageView, trackGoal). Прямые вызовы `window.ym(...)` по
 * компонентам запрещены — так все точки вызова единообразны и легко
 * грепаются.
 */

declare global {
  interface Window {
    ym?: (
      counterId: number | string,
      method: string,
      ...args: unknown[]
    ) => void;
  }
}

// Строка из сборки. Пустая, когда не сконфигурирована (dev, CI-сборки
// без переменной) — это и есть сигнал «аналитика выключена».
const COUNTER_ID = process.env.YANDEX_METRIKA_ID ?? '';

let initialised = false;

export function isMetrikaEnabled(): boolean {
  return COUNTER_ID !== '';
}

export function initMetrika(): void {
  if (initialised) return;
  if (!isMetrikaEnabled()) return;
  if (typeof window === 'undefined') return;

  // Стандартный бутстрап Метрики: ставим заглушку `ym`, которая копит
  // вызовы в очередь, пока не подгрузится настоящий tag.js и не заменит
  // её. Повторяет сниппет из
  // https://yandex.ru/support/metrica/code/counter-initialize.html,
  // но типизирован чисто, без акробатики. Без <noscript> fallback —
  // дашборд это SPA под авторизацией, JS обязателен.
  type StubFn = ((...args: unknown[]) => void) & { a?: unknown[]; l?: number };
  if (!window.ym) {
    const stub: StubFn = (...args: unknown[]) => {
      (stub.a = stub.a || []).push(args);
    };
    stub.l = Date.now();
    window.ym = stub as unknown as Window['ym'];
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://mc.yandex.ru/metrika/tag.js';
  document.head.appendChild(script);

  window.ym?.(COUNTER_ID, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: false,
  });

  initialised = true;
}

export function trackPageView(url: string, title?: string): void {
  if (!isMetrikaEnabled() || typeof window === 'undefined') return;
  window.ym?.(COUNTER_ID, 'hit', url, title ? { title } : undefined);
}

/**
 * Отправка цели — основная часть аналитики идёт через них. Имена целей —
 * константы в `goals.ts`, произвольные строки передавать нельзя.
 */
export function trackGoal(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (!isMetrikaEnabled() || typeof window === 'undefined') return;
  window.ym?.(COUNTER_ID, 'reachGoal', name, params);
}
